import { QueryResult } from "@apollo/client";
import {
  InfiniteMasonry,
  InfiniteMasonrySkeleton,
  Select,
  SelectItem,
  Text,
} from "@archery-inc/design-system";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "@tanstack/react-router";
import {
  AiAnimatedElementsGeneration,
  Exact,
  GenerationsByDetectedObject,
  GenerationWithOrientation,
  GetGenerationsByDetectedObjectsQuery,
  InputMaybe,
  MyGenerationsQuery,
  ObjectDescription,
} from "../__generated__/graphql";
import {
  getAspectRatioString,
  isGenerationProcessing,
  shouldRefetchGeneration,
} from "../api/graphql/generation";
import {
  useGenerationByDetectedObject,
  useGetGenerationsByIds,
  useGetGenerationsComplete,
  useGetGenerationsProgress,
  useMyGenerationsQuery,
} from "../api/graphql/generationList";
import AiGeneration from "../common/AiGeneration";

export default function MyGenerationsPage() {
  const [filter, setFilter] = useState("null");
  const filters = useGenerationByDetectedObject();
  const activeFilter =
    filters.data?.getGenerationsByDetectedObjectsAnonymous.items.find(
      (i) => i.detectedObject === filter,
    ) as GenerationsByDetectedObject;
  return (
    <div className="p-4">
      <GenerationFilters
        filters={filters}
        selectedFilter={filter}
        onFilterUpdate={setFilter}
      />
      {activeFilter ? (
        <FilteredGenerations generations={activeFilter.generations} />
      ) : (
        <GenerationsList />
      )}
    </div>
  );
}

function FilteredGenerations({
  generations,
}: {
  generations: GenerationWithOrientation[];
}) {
  const result = useGetGenerationsByIds(generations.map((g) => g.generationId));
  if (result.loading) {
    return (
      <AnimatePresence>
        <InfiniteMasonry
          items={{
            data: generations.map((g) => ({
              _id: g.generationId,
              ...g,
            })),
            nextCursor: null,
          }}
          itemKey={(g) => g?.generationId}
          limit={Infinity}
          component={Skeletons}
        />
      </AnimatePresence>
    );
  }
  if (!result.data) return null;
  const generationOrder = Object.fromEntries(
    result.data.getGenerationsAnonymous.map((g) => {
      const generation = g as AiAnimatedElementsGeneration;
      return [generation.generationId, generation];
    }),
  );

  return (
    <AnimatePresence>
      <InfiniteMasonry
        items={{
          data: generations.map((g) => generationOrder[g.generationId]),
          nextCursor: null,
        }}
        itemKey={(g) => g?.generationId}
        limit={Infinity}
        component={AiGeneration}
      />
    </AnimatePresence>
  );
}

function Skeletons({
  data,
}: {
  data: GenerationWithOrientation & { _id: string };
}) {
  const aspectRatio = getAspectRatioString(data.aspectRatio);
  return (
    <motion.div
      className="bg-grey-2 w-full rounded-2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ aspectRatio }}
    />
  );
}

function GenerationFilters({
  filters,
  selectedFilter,
  onFilterUpdate,
}: {
  filters: QueryResult<
    GetGenerationsByDetectedObjectsQuery,
    Exact<{
      objectDescriptionToUse?: InputMaybe<ObjectDescription>;
    }>
  >;
  selectedFilter: string;
  onFilterUpdate: (filter: string) => void;
}) {
  if (filters.loading) {
    return <div className="h-14 md:w-20 w-full rounded-2 mb-2 bg-grey-2" />;
  }
  const items = filters.data?.getGenerationsByDetectedObjectsAnonymous.items;
  if (!items?.length) {
    return null;
  }
  return (
    <Select
      selectedKey={selectedFilter}
      onSelectionChange={(e) => onFilterUpdate(e as string)}
      className="mb-2 md:w-fit w-full overflow-hidden"
      popoverClassName="h-96 max-h-fit"
      items={[
        { id: "null", name: <>All</> },
        ...items.map((item) => ({
          id: item.detectedObject,
          name: (
            <>
              {item.previewUrl && (
                <img
                  src={item.previewUrl}
                  className="size-8 object-contain rounded-2"
                />
              )}
              {item.detectedObject}
            </>
          ),
        })),
      ]}
    >
      {(item) => <SelectItem>{item.name}</SelectItem>}
    </Select>
  );
}

function GenerationsList() {
  const limit = 20;
  const listQuery = useMyGenerationsQuery({ limit });
  useGenerationProgressPolling(listQuery, { delay: 1000 });

  if (!listQuery.data) {
    return <InfiniteMasonrySkeleton />;
  }
  if (!listQuery.data.myAiGenerationsAnonymous.data.length) {
    return <NoGeneration />;
  }
  return (
    <InfiniteMasonry
      items={
        listQuery.data.myAiGenerationsAnonymous as {
          data: AiAnimatedElementsGeneration[];
          nextCursor: string | null;
        }
      }
      itemKey={(g) => g?.generationId}
      limit={limit}
      onFetchMore={(cursor) =>
        listQuery.fetchMore({ variables: { cursor, limit } })
      }
      component={AiGeneration}
    />
  );
}

function useGenerationProgressPolling(
  query: QueryResult<MyGenerationsQuery>,
  { delay }: { delay: number },
) {
  const [fetchDoneGenerations] = useGetGenerationsComplete();
  const [fetchGenerationProgress] = useGetGenerationsProgress();

  useEffect(() => {
    if (!query.data) return;

    const getPendingGenerationIds = (
      generations: AiAnimatedElementsGeneration[],
    ) =>
      generations
        .filter(shouldRefetchGeneration)
        .map((generation) => generation.generationId);

    const getDoneGenerationIds = (
      generations: AiAnimatedElementsGeneration[],
    ) =>
      generations
        .filter((g) => !isGenerationProcessing(g))
        .map((generation) => generation.generationId);

    let pendingGenerationIds = getPendingGenerationIds(
      query.data.myAiGenerationsAnonymous
        .data as AiAnimatedElementsGeneration[],
    );

    if (!pendingGenerationIds?.length) {
      return;
    }

    let cleared = false;
    const intervalId = setInterval(() => {
      void fetchGenerationProgress({
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "network-only",
        variables: { generationsIds: pendingGenerationIds },
      }).then(({ data }) => {
        pendingGenerationIds = getPendingGenerationIds(
          data!.getGenerationsAnonymous as AiAnimatedElementsGeneration[],
        );

        // To update output video
        const doneGenerationIds = getDoneGenerationIds(
          data!.getGenerationsAnonymous as AiAnimatedElementsGeneration[],
        );
        if (doneGenerationIds.length) {
          void fetchDoneGenerations({
            notifyOnNetworkStatusChange: true,
            fetchPolicy: "network-only",
            variables: { generationsIds: doneGenerationIds },
          });
        }

        if (!pendingGenerationIds.length) {
          clearInterval(intervalId);
          cleared = true;
        }
      });
    }, delay);

    if (!cleared) {
      return () => clearInterval(intervalId);
    }
  }, [fetchGenerationProgress, query, delay, fetchDoneGenerations]);
}

function NoGeneration() {
  return (
    <div
      className="flex flex-col justify-center items-center"
      style={{ height: "calc(100vh - 80px)" }}
    >
      <img src="/video-play-buttons.png" className="max-w-lg mb-6" />
      <Text className="text-2xl font-medium mb-2">
        <FormattedMessage id="ai_reveals_my_generations.emptystate.title" />
      </Text>
      <Text>
        <FormattedMessage
          id="ai_reveals.my_generations.emptystate.description"
          values={{
            tab: (
              <Link className="font-medium underline" to="/">
                <FormattedMessage id="ai_reveal_create_tab_title" />
              </Link>
            ),
          }}
        />
      </Text>
    </div>
  );
}
